import React, { useState } from 'react'
import { useToast } from 'containers/ToastManager'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { ModalInjectedProps } from 'containers/ModalManager'
import Checkbox from 'components/Checkbox'
import { useVersionUpdateKind } from 'modules/shares/operations'

interface StarUpdateModalProps extends ModalInjectedProps {
  versionIdentifier: string
  isLibrary: boolean
  isNewLatest: boolean
}

export const StarUpdateModal = ({
  hideModal,
  isModalOpen,
  versionIdentifier,
  isLibrary,
  isNewLatest,
}: StarUpdateModalProps) => {
  const { showToast } = useToast()
  const [skipModal, setSkipModal] = useState(false)

  const [versionKindUpdate, { loading }] = useVersionUpdateKind({
    onCompleted: () => {
      showToast(<>A new starred Update has been created</>)
      hideModal()
    },
    onError: message => showToast(message, 'negative'),
  })

  const getCopy = () => {
    if (isLibrary) {
      return isNewLatest ? (
        <p>
          Adding a Star to a Library Update means users subscribed to a Library
          will only receive updates when a save is starred — as well as
          highlighting the update when viewing on a Workspace.
          <br />
          External viewers can only view the starred updates.{' '}
          <a href="http://sketch.com/docs/sketch-cloud/#starred-updates%C2%A0">
            Learn more.
          </a>
        </p>
      ) : (
        'A more recent Starred Update is available in this Library. Starring this Update will not send out any changes. You can always Unstar the more recent update.  Do you want to Star this update anyway?'
      )
    }

    return isNewLatest ? (
      <p>
        Adding a Star to an Update will highlight it when viewing the document
        on a Workspace.
        <br />
        External viewers can only view the starred updates.{' '}
        <a href="http://sketch.com/docs/sketch-cloud/#starred-updates%C2%A0">
          Learn more.
        </a>
      </p>
    ) : (
      'A more recent Starred Update is available in this document and will show first to external Viewers. You can always Unstar the more recent update.  Do you want to Star this update anyway?'
    )
  }

  const handleSkipModal = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSkipModal(event.target.checked)
  }

  const onConfirm = () => {
    versionKindUpdate({ versionIdentifier, kind: 'PUBLISHED' })

    localStorage.setItem(
      'user:preferences:skipStarModal',
      JSON.stringify(skipModal)
    )
  }

  return (
    <ConfirmationDialog
      data-testid="create-star-update-modal"
      title={'Do you want to Star this Update?'}
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      cancelButton={{
        disabled: loading,
      }}
      confirmButton={{
        variant: 'primary',
        text: 'Star Update',
        loading,
        disabled: loading,
      }}
      onConfirm={onConfirm}>
      <p>{getCopy()}</p>
      <Checkbox
        checked={skipModal}
        onChange={handleSkipModal}
        label="Don't ask me again"
      />
    </ConfirmationDialog>
  )
}
